import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../contexts/CartContext';
import { useSEO } from '../hooks/useSEO';

const ThankYouWrapper = styled.div`
  padding: var(--spacing-8) 0;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-8);
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-weight: 600;
  margin-bottom: var(--spacing-4);
  color: var(--color-secondary);
`;

const CheckIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--color-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-6);
  
  &::after {
    content: "✓";
    font-size: 40px;
    font-weight: bold;
    color: white;
  }
`;

const Message = styled.p`
  font-size: var(--font-size-lg);
  margin-bottom: var(--spacing-6);
  color: var(--color-text);
  line-height: 1.6;
`;

const InfoBox = styled.div`
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  margin: var(--spacing-4) 0;
  text-align: left;
`;

const InfoTitle = styled.h3`
  margin: 0 0 var(--spacing-2);
  color: var(--color-secondary);
`;

const InfoText = styled.p`
  margin: var(--spacing-1) 0;
  font-size: var(--font-size-sm);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-4);
  justify-content: center;
  margin-top: var(--spacing-6);
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius);
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &.primary {
    background-color: var(--color-primary);
    color: white;
    
    &:hover {
      background-color: var(--color-primary-dark);
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: var(--color-secondary);
    border: 1px solid var(--color-border);
    
    &:hover {
      background-color: var(--color-background);
    }
  }
`;

const ThankYou: React.FC = () => {
  const location = useLocation();
  const { clearCart } = useCart();
  useSEO({
    entityType: 'page',
    entityId: 'thank-you',
    fallbackTitle: 'Tack för din beställning – Lillaflora',
    fallbackDescription: 'Vi bekräftar din order och kontaktar dig så snart som möjligt. Hela Kramfors får veta att ett paket är på väg.',
    fallbackImage: '/static/images/lillaflora-hero.jpg'
  });
  
  // Clear cart when component mounts (in case user navigated here directly after payment)
  useEffect(() => {
    clearCart();
  }, [clearCart]);

  return (
    <ThankYouWrapper>
      <div className="container">
        <Card>
          <CheckIcon />
          
          <Title>Tack för din beställning!</Title>
          
          <Message>
            Din betalning har genomförts framgångsrikt och din beställning har tagits emot. 
            Du kommer att få en orderbekräftelse via e-post inom kort.
          </Message>
          
          <InfoBox>
            <InfoTitle>Vad händer nu?</InfoTitle>
            <InfoText>• Du får en orderbekräftelse på din e-post</InfoText>
            <InfoText>• Vi börjar förbereda din beställning</InfoText>
            <InfoText>• Du får en avisering när paketet skickas</InfoText>
            <InfoText>• Leveranstid: 2-5 arbetsdagar</InfoText>
          </InfoBox>
          
          <InfoBox>
            <InfoTitle>Behöver du hjälp?</InfoTitle>
            <InfoText>
              Kontakta oss gärna om du har frågor om din beställning eller behöver ändra något.
            </InfoText>
          </InfoBox>
          
          <ButtonGroup>
            <Button to="/" className="primary">
              Till startsidan
            </Button>
            <Button to="/produkter" className="secondary">
              Fortsätt handla
            </Button>
          </ButtonGroup>
        </Card>
      </div>
    </ThankYouWrapper>
  );
};

export default ThankYou;
